import { useCallback, useEffect, useState } from "react";
import { X, UserPlus, KeyRound, Trash2, Loader2, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/dialogs";

export default function AdminUsersDialog({ open, onClose, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await api.listUsers());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const addUser = async (e) => {
    e.preventDefault();
    if (!newEmail || newPassword.length < 8) {
      toast.error("Valid email + ≥8 char password required");
      return;
    }
    try {
      await api.createUser({ email: newEmail.trim(), password: newPassword, role: newRole });
      toast.success(`Created ${newEmail}`);
      setNewEmail(""); setNewPassword(""); setNewRole("user"); setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const resetPw = async (u) => {
    const pw = await promptDialog({ title: `Reset password for ${u.email}`, message: "Minimum 8 characters. The user will be flagged to change it on next login.", confirmLabel: "Set new password" });
    if (!pw || pw.length < 8) return;
    try {
      await api.resetUserPassword(u.id, pw);
      toast.success(`Password reset · share securely: ${pw}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const removeUser = async (u) => {
    const ok = await confirmDialog({ title: `Delete ${u.email}?`, message: "Their projects will be reassigned to you.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteUser(u.id);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[900] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-overlay">
      <div className="bg-[#050805] border border-[#2D7544] w-full max-w-2xl max-h-[85vh] flex flex-col shadow-[6px_6px_0px_#00FF41]" onClick={(e) => e.stopPropagation()} data-testid="admin-dialog">
        <div className="flex items-center justify-between border-b border-[#2D7544] px-5 py-3">
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-[#00FF41]" />
            <div className="dv-overline">user management</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#0D1F12]" data-testid="admin-close"><X size={14} /></button>
        </div>

        <div className="px-5 py-3 border-b border-[#2D7544] flex items-center justify-between">
          <div className="font-mono text-[11px] text-[#66B07A]">{users.length} user{users.length === 1 ? "" : "s"}</div>
          <button onClick={() => setShowAdd((s) => !s)} className="dv-btn-primary flex items-center gap-1" data-testid="show-add-user">
            <UserPlus size={13} /> {showAdd ? "Cancel" : "Add user"}
          </button>
        </div>

        {showAdd && (
          <form onSubmit={addUser} className="px-5 py-4 border-b border-[#2D7544] grid grid-cols-1 md:grid-cols-3 gap-3 bg-[#000000]" data-testid="add-user-form">
            <input data-testid="new-user-email" placeholder="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="dv-input" type="email" autoFocus />
            <input data-testid="new-user-password" placeholder="temp password (8+ chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="dv-input" type="text" />
            <div className="flex gap-2">
              <select data-testid="new-user-role" value={newRole} onChange={(e) => setNewRole(e.target.value)} className="dv-input flex-1"><option value="user">user</option><option value="admin">admin</option></select>
              <button data-testid="submit-new-user" type="submit" className="dv-btn-primary">Create</button>
            </div>
          </form>
        )}

        <div className="flex-1 overflow-y-auto" data-testid="user-list">
          {loading ? (
            <div className="p-10 text-center text-[#66B07A]"><Loader2 className="animate-spin inline mr-2" size={14} /> loading…</div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-[#66B07A] text-xs">no users yet</div>
          ) : (
            <ul className="divide-y divide-[#1F4D2A]">
              {users.map((u) => (
                <li key={u.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {u.role === "admin" ? <ShieldCheck size={12} className="text-[#00FF41]" /> : null}
                      <div className="text-sm text-[#00FF41] truncate">{u.email}</div>
                      {u.id === currentUserId && <span className="text-[10px] font-mono uppercase tracking-widest text-[#3D8657]">(you)</span>}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#3D8657] mt-1">
                      {u.role} · {u.must_change_password ? "must change pw" : "active"} · since {String(u.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => resetPw(u)} className="dv-btn-secondary flex items-center gap-1" data-testid={`reset-pw-${u.id}`} title="Reset password">
                      <KeyRound size={12} /> reset
                    </button>
                    {u.id !== currentUserId && (
                      <button onClick={() => removeUser(u)} className="dv-btn-secondary flex items-center gap-1 hover:!border-[#FF3344] hover:!text-[#FF3344]" data-testid={`delete-user-${u.id}`} title="Delete">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
